import type { VariationOrder } from './types';

/**
 * S4-T11 — Centralized VO 6-stage taxonomy.
 *
 * Six happy-path stages plus `rejected` as a terminal off-ramp.
 * Old `reviewed` is split into `techReviewed` + `commercialReviewed`;
 * old `approved` becomes `clientApproved` (with `posted` and `recomputed`
 * as downstream lifecycle states).
 *
 * Selectors and dashboards use `isVoApproved` / `isVoOpen` as the semantic
 * predicates so they don't have to enumerate the union manually — that
 * insulates them from future taxonomy tweaks.
 */

export type VoStatus = VariationOrder['status'];

/** The six happy-path stages, in lifecycle order. `rejected` is excluded. */
export const VO_HAPPY_STAGES = [
  'initiated',
  'techReviewed',
  'commercialReviewed',
  'clientApproved',
  'posted',
  'recomputed',
] as const satisfies readonly VoStatus[];

/** Stages from clientApproved onward count as "approved" for selectors. */
const APPROVED_STAGES = new Set<VoStatus>(['clientApproved', 'posted', 'recomputed']);

/** True if the VO has progressed past client approval (counts toward profitability). */
export function isVoApproved(status: VoStatus): boolean {
  return APPROVED_STAGES.has(status);
}

/** True while the VO is still in motion — neither approved nor rejected. */
export function isVoOpen(status: VoStatus): boolean {
  return !APPROVED_STAGES.has(status) && status !== 'rejected';
}

/**
 * Position of `status` in the happy-path strip.
 *   - 0..5 for the six stages
 *   - -1 for `rejected` (off-path)
 */
export function voStageIndex(status: VoStatus): number {
  const idx = VO_HAPPY_STAGES.indexOf(status as (typeof VO_HAPPY_STAGES)[number]);
  return idx;
}

/** Owner role for each stage — drives the LifecycleStrip tooltips. */
export const VO_STAGE_OWNER: Record<(typeof VO_HAPPY_STAGES)[number], string> = {
  initiated: 'contractsAdmin',
  techReviewed: 'engineeringDirector',
  commercialReviewed: 'contractsAdmin',
  clientApproved: 'contractsAdmin',
  posted: 'financeManager',
  recomputed: 'financeManager',
};
